import type { MenuDTO } from "../dto/menu.dto";

type Mode = "LOCAL" | "DELIVERY";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;

// single-flight caches — visual content no se cachea (siempre fresco)
const menuPromiseCache = new Map<string, Promise<MenuDTO>>();
const featuresPromiseCache = new Map<string, Promise<FeatureDTO[]>>();

export type FeatureDTO = { name: string; priority: number; items: string[] };

function baseUrl(restaurantId: string) {
  if (!SUPABASE_URL) throw new Error("VITE_SUPABASE_URL is required");
  return `${SUPABASE_URL}/storage/v1/object/public/menu-data/${restaurantId}`;
}



export async function getMenuDto(
  restaurantId: string,
  versionIso: string,
  mode: Mode,
  signal?: AbortSignal,
  opts?: { force?: boolean }
): Promise<MenuDTO> {
  const v = new Date(versionIso).getTime();
  const key = `${restaurantId}:${mode}:${v}`;

  if (!opts?.force) {
    const cached = menuPromiseCache.get(key);
    if (cached) return cached;
  }

  const modeFile = `${mode.toLowerCase()}.json`;

  const p = (async () => {
    const res = await fetch(`${baseUrl(restaurantId)}/${modeFile}?v=${v}`, {
      signal,
      cache: "no-store",
    });
    if (!res.ok) throw new Error("Menú no disponible");
    const dto = await res.json() as MenuDTO;
   // Guard mínimo — si la EF hizo su trabajo esto nunca se dispara
   if (!dto?.categories || !Array.isArray(dto.categories)) {
    throw new Error("Menú con estructura inválida");
  }
  
  return dto;
  })();

  menuPromiseCache.set(key, p);

  try {
    return await p;
  } catch (e) {
    menuPromiseCache.delete(key);
    throw e;
  }
}

export async function getFeaturesDto(
  restaurantId: string,
  mode: Mode,
  versionIso: string,
  signal?: AbortSignal,
  opts?: { force?: boolean }
): Promise<FeatureDTO[]> {
  const v = new Date(versionIso).getTime();
  const key = `${restaurantId}:features:${mode}:${v}`;

  if (!opts?.force) {
    const cached = featuresPromiseCache.get(key);
    if (cached) return cached;
  }

  const p = (async () => {
    const modeFile = `features_${mode.toLowerCase()}.json`;
    const res = await fetch(`${baseUrl(restaurantId)}/${modeFile}?v=${v}`, {
      signal,
      cache: "no-store",
    });
    if (!res.ok) return [];
    const json = await res.json();
    return Array.isArray(json) ? (json as FeatureDTO[]) : [];
  })();

  featuresPromiseCache.set(key, p);

  try {
    return await p;
  } catch (e) {
    featuresPromiseCache.delete(key);
    throw e;
  }
}

export async function getVisualContentRaw(
  restaurantId: string,
  mode: 'LOCAL' | 'DELIVERY',
  signal?: AbortSignal,
): Promise<any[]> {
  const v = Date.now();
  const activeField = mode === 'DELIVERY' ? 'is_active_delivery' : 'is_active_local';

  const safeFetch = async (filename: string): Promise<any[] | null> => {
    const res = await fetch(
      `${baseUrl(restaurantId)}/${filename}?v=${v}`,
      { signal, cache: "no-store" }
    );
    if (!res.ok) return null;
    const json = await res.json();
    return Array.isArray(json) ? json : [];
  };

  // Formato nuevo unificado: un solo archivo con is_active_local / is_active_delivery
  const unified = await safeFetch('visual_content.json').catch((e: unknown) => {
    if ((e as Error)?.name === 'AbortError') throw e;
    return null;
  });
  if (unified !== null) {
    return unified.filter((item: any) =>
      activeField in item ? item[activeField] === true : (item.is_active ?? true) === true
    );
  }

  // Fallback: archivos viejos por canal (formato legacy)
  const modeFile = mode === 'DELIVERY' ? 'visual_content_delivery.json' : 'visual_content_local.json';
  const legacy = await safeFetch(modeFile).catch((e: unknown) => {
    if ((e as Error)?.name === 'AbortError') throw e;
    return null;
  });
  return (legacy ?? []).filter((item: any) => (item.is_active ?? true) === true);
}

export function clearRestaurantCache(restaurantId: string) {
  for (const k of menuPromiseCache.keys()) {
    if (k.startsWith(restaurantId + ":")) menuPromiseCache.delete(k);
  }
  for (const k of featuresPromiseCache.keys()) {
    if (k.startsWith(restaurantId + ":")) featuresPromiseCache.delete(k);
  }
}

export async function fetchMenuDtoFresh(
  restaurantId: string,
  mode: Mode,
): Promise<MenuDTO | null> {
  try {
    const res = await fetch(
      `${baseUrl(restaurantId)}/${mode.toLowerCase()}.json`,
      { cache: 'no-store' }
    );
    if (!res.ok) return null;
    const dto = await res.json() as MenuDTO;
    if (!dto?.metadata?.last_updated_iso) return null;
    return dto;
  } catch {
    return null;
  }
}